// Refactored From: {nhsjobs-baseurl}/NHSJobs/frontend/search-frontend/-/blob/rc-3.4.6/app/assets/javascript/location-autocomplete.js
import axios from 'axios';
import accessibleAutocomplete from 'accessible-autocomplete';

/**
 * Data8 source for accessibleAutocomplete()
 * Calls {Data8-webservice-host-url}/PredictiveLocation/Search.json via /candidate/search/locationSearch
 * @param {String}   query            user input sent to API
 * @param {Function} populateResults  callback function provided by accessibleAutocomplete()
 * @returns {Promise<void>}
 */
async function locationAutocompleteCallback(query, populateResults) {
  let results = []
  try {
    const response = await axios({
      method: 'GET',
      url: `/candidate/search/locationSearch?text=${query}`,
      responseType: 'json',
      headers: { 'Content-Type': 'application/json' }  // Chrome: Refused to set unsafe header "Accept-Encoding"
    });
    results = response && response.data || []
    results = results
      .map(function(result) { return String(result).trim() })
      .filter(function(result) { return !!result })
      .splice(0, 8)  // 1920x1080 is max 12 single-line results - 8 is more human-readable
    ;
    if( results.length === 1 && results[0] === query ) { results = []; }  // Don't show exact match if only result
    populateResults(results);
  } catch (exception) {
    console.error('locationAutocompleteCallback()', query, exception);
    populateResults([]);
  }
}


/**
 * accessibleAutocomplete() with sensible defaults
 * @param {Element} element
 * @param {Object}  options
 */
function accessibleAutocompleteDefaults(element, options={}) {

  // NOTE: <input> is not visible in HTML until accessibleAutocomplete() is called
  // NOTE: if <input> renders twice - because accessibleAutocomplete() is called twice
  // DOCS: https://github.com/alphagov/accessible-autocomplete
  accessibleAutocomplete({
    source: locationAutocompleteCallback,
    element:      element,
    id:           "location",   // <input id="">   | used by locationDistance.mjs
    name:         "location",   // <input name="">
    minLength: 3,               // Chars required before dropdown appears
    showNoOptionsFound: false,  // Avoid flashing no-results message
    autoselect: false,          // highlight first autocomplete item and allow quick enter
    // defaultValue: '',        // element.querySelector('input').value() || ''
    // confirmOnBlur: true,     // Seems to have no effect
    // onConfirm: () => {},     // Callback
    ...options,
  });
}

/**
 * Bind accessibleAutocomplete() using progressive-enhancement to copying over HTML <input> template values
 * NOTE: child input does not need <noscript> wrapper, but will be removed and replaced by accessibleAutocomplete()
 * NOTE: value="{{ currentQuery.location }}" automatically applies he.decode() to url encoded values (eg: M%27cstr,%20Salford)
 * HTML template:
 *   <div id="autocomplete-container">
 *     <input name="location" id="location" value="{{ currentQuery.location }}" data-test="search-location-input" class="nhsuk-input nhsuk-u--width-full" type="text" />
 *   </div>
 *
 * @param {Element} element
 * @param {Object}  options
 */
function accessibleAutocompleteInput(element, options={}) {
  if (!element) { return; }
  // progressive-enhancement: copy over defaultValue
  const noscript_input = element.querySelector('input');
  const default_value  = noscript_input && noscript_input.value || ''
  accessibleAutocompleteDefaults(element, { ...options, defaultValue: default_value })

  // Copy over attributes to newly created <input> before deleting original <input>
  // NOTE: .setAttribute() does not trigger onChange event (causing dropdown to trigger onload)
  const autocomplete_input = element.querySelectorAll('input')[1];
  for (const attribute of noscript_input.attributes) {
    if( attribute.name === 'value' ) { continue; }  // Skip value attribute (already passed to defaultValue)
    autocomplete_input.setAttribute(attribute.name, attribute.value);
  }
  noscript_input.remove();
}


/**
 * Binds accessibleAutocomplete() to #autocomplete-container #autocomplete-mobile-container .nhsuk-locationAutocomplete
 */
function bindLocationAutocomplete() {
  // NOTE: ids = [ location, location_mobile ] preserved from location-autocomplete.js codebase
  const autocomplete_container        = document.getElementById('autocomplete-container');
  const autocomplete_mobile_container = document.getElementById('autocomplete-mobile-container');
  accessibleAutocompleteInput(autocomplete_container,        { id: "location" });
  accessibleAutocompleteInput(autocomplete_mobile_container, { id: "location_mobile" });

  // @Unused - for future autocomplete boxes
  document.querySelectorAll('.nhsuk-locationAutocomplete')
    .forEach(element => accessibleAutocompleteInput(element, {}))
  ;
}

/**
 * Binds distance <select> enable/disable toggle based on location input
 */
function bindDistanceSelect() {
  const locationInput  = document.getElementById('location') || document.getElementById('location_mobile')
  const distanceSelect = document.getElementById('distance')
  if( locationInput && distanceSelect ) {
    if( locationInput.value === '' ) { distanceSelect.disabled = true }
    locationInput.addEventListener('keyup', function (event) {
      if( event.target.value.trim() === '' ) {
        distanceSelect.disabled = true
        distanceSelect.value = ''
      } else {
        distanceSelect.disabled = false
        if( distanceSelect.value === '' ) {
          distanceSelect.value = 5
          distanceSelect.lastElementChild.disabled = 'disabled'
        }
      }
    })
  }
}


export {
  bindLocationAutocomplete,
  bindDistanceSelect,
  locationAutocompleteCallback,
  accessibleAutocompleteInput,
  accessibleAutocompleteDefaults
};
export default () => {
  bindLocationAutocomplete()
  bindDistanceSelect()
};
